import { useNavigate } from 'react-router-dom';
import { Trash2, Wand2 } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/lib/formatters';
import type { SetupDraft } from '@/types/setup-wizard.types';
import {
  DRAFT_STEP_TOTAL,
  draftSetupPath,
  draftTitle,
  sortDraftsByRecency,
} from '../utils/setupDraft';


function draftProgress(draft: SetupDraft): number {
  return draft.completedSteps?.length ?? 0;
}

function DraftProgressBar({ completed }: { completed: number }): React.ReactElement {
  const percent = Math.min(100, Math.round((completed / DRAFT_STEP_TOTAL) * 100));
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1.5 w-24 overflow-hidden rounded-full bg-amber-200/70 dark:bg-amber-400/20"
        role="progressbar"
        aria-valuenow={completed}
        aria-valuemin={0}
        aria-valuemax={DRAFT_STEP_TOTAL}
        aria-label="Setup progress"
      >
        <div
          className="h-full rounded-full bg-amber-500 transition-[width] dark:bg-amber-400"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs font-medium text-amber-900 dark:text-amber-200">
        {completed}/{DRAFT_STEP_TOTAL} steps
      </span>
    </div>
  );
}

export interface ResumeSetupBannerProps {
  drafts: SetupDraft[];
  /** Omit to hide the discard action (e.g. on the dashboard summary). */
  onDiscard?: (draft: SetupDraft) => void;
  discardPending?: boolean;
  /** `compact` drops the intro copy for dense placements like the dashboard. */
  variant?: 'default' | 'compact';
  className?: string;
}

/**
 * Amber "you left a company setup unfinished" banner. Rendered above the page
 * content so an abandoned draft is the first thing seen on return, rather than
 * something to scroll for. Drafts are listed most-recently-edited first.
 */
export function ResumeSetupBanner({
  drafts,
  onDiscard,
  discardPending = false,
  variant = 'default',
  className,
}: ResumeSetupBannerProps): React.ReactElement | null {
  const navigate = useNavigate();
  if (drafts.length === 0) return null;

  const ordered = sortDraftsByRecency(drafts);
  const compact = variant === 'compact';

  return (
    <section
      aria-label="Unfinished company setups"
      className={cn(
        'rounded-xl border border-amber-300 bg-amber-50/80 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:border-amber-400/30 dark:bg-amber-400/10',
        className
      )}
    >
      {!compact ? (
        <div className="flex items-center justify-between gap-3 border-b border-amber-200 px-4 py-3 dark:border-amber-400/25">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-950 dark:text-amber-100">
              <Wand2 className="h-4 w-4 shrink-0" aria-hidden />
              {ordered.length === 1
                ? 'You have an unfinished company setup'
                : `You have ${ordered.length} unfinished company setups`}
            </h2>
            <p className="mt-0.5 text-xs text-amber-900/80 dark:text-amber-200/80">
              Pick up where you left off, or discard the draft to start fresh. Nothing goes live
              until you launch.
            </p>
          </div>
        </div>
      ) : null}
      <div className="divide-y divide-amber-200/70 dark:divide-amber-400/20">
        {ordered.map((draft) => {
          const lastEdited = draft.updatedAt ?? draft.createdAt;
          return (
            <div
              key={draft._id}
              className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate text-sm font-semibold text-amber-950 dark:text-amber-100">
                    {draftTitle(draft)}
                  </h3>
                  <Badge variant="warning" size="sm">
                    Draft setup
                  </Badge>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-amber-900/80 dark:text-amber-200/80">
                  {draft.companyDraft?.code ? (
                    <span className="font-mono">{draft.companyDraft.code}</span>
                  ) : (
                    <span>Code not set</span>
                  )}
                  <span aria-hidden className="text-amber-700/40 dark:text-amber-300/30">
                    ·
                  </span>
                  <DraftProgressBar completed={draftProgress(draft)} />
                  {lastEdited ? (
                    <>
                      <span aria-hidden className="text-amber-700/40 dark:text-amber-300/30">
                        ·
                      </span>
                      <span>Last edited {formatRelative(lastEdited)}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Button type="button" size="sm" onClick={() => navigate(draftSetupPath(draft))}>
                  <Wand2 className="h-4 w-4" aria-hidden />
                  <span className="ml-1.5">Continue setup</span>
                </Button>
                {onDiscard ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => onDiscard(draft)}
                    disabled={discardPending}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    <span className="ml-1.5">Discard</span>
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

ResumeSetupBanner.displayName = 'ResumeSetupBanner';
